// Imports from React, MUI, custom helpers, Redux, and project files
import * as React from "react";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  Skeleton,
  Tooltip,
} from "@mui/material";
import "./tableStyles.css";
import {
  BasicSelect,
  formatDateTime,
  SearchInput,
  useDebounce,
} from "../../utils/helperdata";
import { useNavigate } from "react-router-dom";
import CustomPagination from "./customPagination";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteConversationReduc,
  fetchAllConversationSearchAPI,
} from "../../redux/action/UserAction";
import FormDialog from "./tableFilter";
import {
  dateLabelReduc,
  searchStringValueReduc,
  setDateRange,
  userSelector,
} from "../../redux/slices/userSlice";

// Table header configuration
const headCells = [
  { id: "queries", label: "Query" },
  { id: "Subject", label: "Subject" },
  { id: "Createdby", label: "Created By" },
  { id: "Createdon", label: "Created On" },
  { id: "actions", label: "Actions" },
];

// Custom Table Header Component
function EnhancedTableHead() {
  return (
    <TableHead sx={{ backgroundColor: "#1B2559", borderRadius: "10px" }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="left"
            sx={{
              color: "white",
              fontWeight: "bold",
              fontSize: "15px",
              fontFamily: "poppins",
              "&:first-of-type": { borderTopLeftRadius: "10px" },
              "&:last-of-type": { borderTopRightRadius: "10px" },
            }}
          >
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

// Main Table Component
export default function EnhancedTable({ tableData, fullData }) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [showFilter, setShowFilter] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const { convoLoading, searchValue, endDate, startDate, dateLabel } =
    useSelector(userSelector);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userType = sessionStorage.getItem("userType");
  const groupId = sessionStorage.getItem("currentGrp");

  const debouncedQuery = useDebounce(searchValue, 500);

  // Fetch table data on query change
  React.useEffect(() => {
    dispatch(
      fetchAllConversationSearchAPI(
        null,
        searchValue,
        startDate,
        endDate,
        null,
        null
      )
    );
  }, [debouncedQuery]);

  const handleChangePage = (event, newPage) => {
    dispatch(fetchAllConversationSearchAPI(newPage));
    setPage(newPage);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setPage(0);
  };

  const handleNavigate = (convoId) => {
    navigate("/response", { state: { id: convoId, isHistory: true } });
  };

  const handleDeleteConversation = (id) => {
    dispatch(deleteConversationReduc(id, navigate));
  };

  const handleManagerFilter = () => setOpen(true);

  const handleClearFilter = () => {
    dispatch(fetchAllConversationSearchAPI(null));
    dispatch(setDateRange({ startDate: "", endDate: "" }));
    dispatch(searchStringValueReduc({ data: "" }));
    dispatch(dateLabelReduc({ data: "" }));
    setShowFilter(false);
  };

  const nameTrim = (name) =>
    name.length > 40 ? name.substring(0, 40) + "..." : name;

  const dateFilter = {
    labelText: "Select Date",
    labelIcon: (
      <CalendarMonthOutlinedIcon
        sx={{
          mr: 0,
          color: "gray",
          // width: "16rem !important",
          borderRadius: "19px !important",
          fontSize: "1rem ",
          height: "0px ",
          padding: "12px ",
        }}
      />
    ),
    options: [
      { optionLabel: "Today", optionValue: "Today" },
      { optionLabel: "Yesterday", optionValue: "Yesterday" },
      { optionLabel: "Past Week", optionValue: "Past Week" },
      { optionLabel: "Past Month", optionValue: "Past Month" },
    ],
  };

  return (
    <>
      <Box sx={{ width: "90%" }}>
        {/* Filter & Search UI */}
        <div className="search-wrapper">
          {userType === "manager" ? (
            <div className="filter-parent">
              <div className="filter-wrapper" onClick={handleManagerFilter}>
                <FilterAltOutlinedIcon /> Filter
              </div>
              {startDate && showFilter && (
                <div className="clear-filter" onClick={handleClearFilter}>
                  <p>Clear Filter</p>
                </div>
              )}
            </div>
          ) : (
            <div className="filter-parent">
              <BasicSelect
                {...dateFilter}
                dispatch={dispatch}
                setDateRange={setDateRange}
                groupId={groupId}
                selectedDateLabel={dateLabel}
                searchVal={searchValue}
              />
              {startDate && endDate && (
                <div className="clear-filter" onClick={handleClearFilter}>
                  <p>Clear Filter</p>
                </div>
              )}
            </div>
          )}
          <SearchInput searchText={searchValue} dispatch={dispatch} />
        </div>

        {/* Table Container */}
        <Paper sx={{ width: "100%", mb: 2 }}>
          <TableContainer sx={{ width: "100%" }}>
            <Table
              sx={{ tableLayout: "fixed", width: "100%" }}
              aria-labelledby="tableTitle"
            >
              <EnhancedTableHead />
            </Table>

            {/* Scrollable TableBody */}
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                <TableBody>
                  {convoLoading ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Skeleton animation="wave" />
                      </TableCell>
                    </TableRow>
                  ) : tableData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        sx={{ fontSize: "25px" }}
                      >
                        No Data
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableData.map((row) => (
                      <TableRow
                        key={row.conversation_id}
                        hover
                        sx={{ height: "50px" }}
                      >
                        <TableCell>
                          <div className="query-title">
                            {nameTrim(row?.query)}
                          </div>
                        </TableCell>
                        <TableCell align="left">{row?.subject}</TableCell>
                        <TableCell align="left">
                          {row?.created_by.name}
                        </TableCell>

                        <TableCell align="left">
                          {formatDateTime(row?.created_on)}
                        </TableCell>
                        <TableCell>
                          <div className="actions-wrapper">
                            <Tooltip title="Click to view the full response">
                              <RemoveRedEyeOutlinedIcon
                                onClick={() =>
                                  handleNavigate(row?.conversation_id)
                                }
                                style={{ color: "#1B2559", cursor: "pointer" }}
                              />
                            </Tooltip>
                            <Tooltip title="Click to delete the query">
                              <DeleteOutlineOutlinedIcon
                                onClick={() =>
                                  handleDeleteConversation(row?.conversation_id)
                                }
                                style={{ color: "red", cursor: "pointer" }}
                              />
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TableContainer>

          {/* Pagination */}
          <div style={{ padding: "10px" }}>
            {/* <CustomPagination
              count={Math.ceil(fullData?.total_records / rowsPerPage)}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleRowsPerPageChange}
              fullData={fullData}
            /> */}
            {!convoLoading && tableData.length > 0 && (
              <div style={{ padding: "10px" }}>
                <CustomPagination
                  count={Math.ceil(fullData?.total_records / rowsPerPage)}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  fullData={fullData}
                />
              </div>
            )}
          </div>
        </Paper>
      </Box>

      {/* Manager filter modal */}
      <FormDialog
        open={open}
        setOpen={setOpen}
        selectedDateLabel={dateLabel}
        setShowFilter={setShowFilter}
      />
    </>
  );
}
